import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';
import { STEP_LABEL } from './reviewStage';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

interface Props {
  cycle: ReviewCycle;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onSubmitted: () => void;
  /** The HAND-OVER completed — leave the cycle and flash on the dashboard.
   *  Distinct from onSubmitted, which only refetches: a producer who merely
   *  updated their perspective still has the cycle open in front of them,
   *  while one who marked it ready to distribute has nothing left to do here.
   *  Falls back to onSubmitted when not supplied. */
  onHandedOver?: (flash: string) => void;
  /** Embedded items this producer has not confirmed reading. The backend
   *  refuses the hand-over while any remain (CHILDREN_NOT_REVIEWED). */
  unreadItems?: string[];
  /** Switch the viewer to a child tab, so the panel can send the producer
   *  straight to the item it is naming. */
  onOpenItem?: (childCycleId: string) => void;
  childIdByName?: Record<string, string>;
}

/**
 * The producer's commercial perspective — 2026-08-24 (Fredrik).
 *
 * The producer sits at the END of the sequence and is not making a medical or
 * legal risk judgement, so they get neither the risk assessment form nor a
 * thread-starting affordance. What they have instead is this: read what the
 * reviewers found and suggested, then give the decision maker an overall
 * commercial read-back. It is one more input the DM weighs alongside the
 * reviewer assessments, not a risk level of its own.
 *
 * It renders in the same slot where reviewers see "Your Risk Assessment", for
 * the same reason step completion moved there on 2026-08-21: it is the place
 * on the page where the person whose turn it is finishes their turn.
 *
 * Two acts, one button, mirroring "Complete Review": when the producer step is
 * next, submitting the read-back and marking the cycle ready to distribute
 * happen together. The backend enforces the same pairing (READBACK_REQUIRED on
 * the step-complete route), so an API caller cannot skip the read-back either.
 */
export default function ProducerReadbackPanel({
  cycle, getAuthHeaders, onSubmitted, onHandedOver,
  unreadItems = [], onOpenItem, childIdByName = {}
}: Props) {
  const existing = cycle.producerReadback;
  const [text, setText] = useState(existing?.text || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stepComplete = (cycle.stepCompletions || []).some(sc => sc.step === 'producer');
  const isDecided = cycle.status === 'approved'
    || cycle.status === 'revision_needed'
    || cycle.status === 'rejected';

  // Whose turn is it? Same derivation MarkStepCompleteButton uses, so the two
  // never disagree about whether the producer may hand over.
  const sorted = [...(cycle.reviewSequence || [])].sort((a, b) => a.order - b.order);
  const completed = new Set((cycle.stepCompletions || []).map(sc => sc.step));
  const nextStep = sorted.find(s => !completed.has(s.step));
  const isMyTurn = nextStep?.step === 'producer';
  // The hand-over is blocked while embedded items are unread. Blocking here as
  // well as server-side is not belt-and-braces for its own sake: the button
  // submits the read-back FIRST, so letting the click through would save the
  // text and then fail, which reads as "it didn't work" when half of it did.
  const blockedByItems = unreadItems.length > 0;

  const submittedAt = existing?.submittedAt ? new Date(existing.submittedAt) : null;

  async function submit(alsoCompleteStep: boolean) {
    const trimmed = text.trim();
    if (!trimmed) { setError('Write your commercial perspective before submitting.'); return; }
    if (alsoCompleteStep && !window.confirm(
      'Mark ready to distribute?\n\nThis sends your commercial perspective to the decision maker and hands the review on. You can still update the perspective until they decide.'
    )) return;

    setBusy(true); setError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/producer-readback`,
        { text: trimmed },
        { headers }
      );
      if (alsoCompleteStep) {
        await axios.post(
          `${backendUrl}/api/content-review/cycles/${cycle._id}/steps/producer/complete`,
          {},
          { headers }
        );
        if (onHandedOver) {
          // Mirrors what a reviewer gets from "Complete Review"
          // (ReviewInterface's submitAssessment): back to the dashboard with a
          // flash naming who holds it now. Derived the same way rather than
          // hardcoded "the decision maker" — the sequence can collapse to
          // [producer] alone (F3), and two copies of this rule would drift.
          const done = new Set([...(cycle.stepCompletions || []).map(sc => sc.step), 'producer']);
          const after = sorted.find(s => !done.has(s.step));
          onHandedOver(
            after
              ? `Ready to distribute — handed over to ${STEP_LABEL[after.step as keyof typeof STEP_LABEL] || after.step}.`
              : 'Ready to distribute — awaiting the decision maker.'
          );
          return;
        }
      }
      onSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit your commercial perspective');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="review-section" data-testid="producer-readback">
      <h3>Your commercial perspective</h3>

      <div className="viewer-tab-hint">
        Read what the reviewers found and suggested above, then give the decision maker your
        overall read on this piece from the commercial side. This is <strong>not</strong> a
        medical or legal risk assessment — it is your view of the content as the person who
        made it, and the decision maker weighs it alongside the reviewers' assessments.
      </div>

      {stepComplete && (
        <div
          data-testid="producer-readback-done"
          style={{ background: '#e0e7ff', padding: 14, borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}
        >
          <span style={{ fontSize: 18, color: '#3730a3' }}>&#10003;</span>
          <div>
            <strong style={{ color: '#3730a3' }}>Marked ready to distribute</strong>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
              {isDecided
                ? 'The decision maker has decided on this version.'
                : 'Awaiting the decision maker. You can still update your perspective until they decide.'}
            </p>
          </div>
        </div>
      )}

      {submittedAt && (
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
          Last submitted {submittedAt.toLocaleString()}
          {existing?.submittedBy?.name ? ` by ${existing.submittedBy.name}` : ''}.
        </p>
      )}

      <div className="review-form-group">
        <label>Comments to the decision maker</label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="How does this land commercially? Anything the decision maker should weigh against the reviewers' findings..."
          rows={4}
          disabled={isDecided || busy}
        />
      </div>

      {error && (
        <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* A disabled button with no explanation is the thing we keep fixing.
          Name the items, and make each one a way to get there. */}
      {blockedByItems && isMyTurn && !stepComplete && (
        <div
          data-testid="producer-items-outstanding"
          style={{ padding: '12px 14px', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#664d03' }}
        >
          <strong>
            {unreadItems.length} embedded item{unreadItems.length === 1 ? '' : 's'} still to read.
          </strong>
          <p style={{ margin: '4px 0 8px' }}>
            This review's findings are split across its tabs — the assessment above covers the main
            content only. Read the feedback on each item and confirm it there, then you can mark
            this ready to distribute.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {unreadItems.map(name => {
              const id = childIdByName[name];
              return id && onOpenItem ? (
                <button
                  key={name}
                  className="review-btn review-btn-outline"
                  style={{ fontSize: 12, padding: '4px 10px' }}
                  onClick={() => onOpenItem(id)}
                >
                  Open {name}
                </button>
              ) : (
                <span key={name} style={{ fontStyle: 'italic' }}>{name}</span>
              );
            })}
          </div>
        </div>
      )}

      {!isDecided && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {isMyTurn && !stepComplete ? (
              <button
                className="review-btn review-btn-success"
                onClick={() => submit(true)}
                disabled={!text.trim() || busy || blockedByItems}
                style={{ fontSize: 15, padding: '10px 24px' }}
                title="Sends your commercial perspective to the decision maker and marks the piece ready for the distribution flow."
              >
                {busy ? 'Submitting...' : 'Mark ready to distribute'}
              </button>
            ) : (
              <button
                className="review-btn review-btn-success"
                onClick={() => submit(false)}
                disabled={!text.trim() || busy}
                style={{ fontSize: 15, padding: '10px 24px' }}
              >
                {busy ? 'Saving...' : submittedAt ? 'Update perspective' : 'Save perspective'}
              </button>
            )}
          </div>

          {!isMyTurn && !stepComplete && (
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              {nextStep
                ? `The ${nextStep.step} review is still open — you can write your perspective now and mark the piece ready to distribute once it reaches you.`
                : 'You can write your perspective now; the hand-over becomes available when your step comes round.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}
